import { FocusTrapZone, Stack, useTheme } from "@fluentui/react";
import React, { useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Measures } from "../../../telemetry/Measures";
import { Trace } from "../../../telemetry/Trace";
import { dashboard } from "../../../telemetry/TraceLogging";
import { ContentReadyContext } from "../../contexts/ContentReadyContext";
import { addVisibilityEventListeners, getIsWindowVisible } from "../../externalApi/WindowApi";
import { getResources } from "../../getResources";
import { lazyLoadModule } from "../../lazyLoadModule";
import { getFeedsInstancePromise, getWidgetElementAsync, onWidgetHidden, onWidgetVisible } from "../../webUXApi/widgetApi";
import { getWidgetOrchestrator } from "../../widgetRuntime/orchestrator/orchestrator";
import { ErrorComponent } from "../ErrorComponent/ErrorComponent";
import { FocusZone } from "../FocusZone/FocusZone";
import { getContainerStyle, getFeedContainerAndWidgetFrameBumperStyle, getFeedErrorComponentStyle, getFeedErrorStateStyle, } from "./FeedContainer.styles";
import { isFeatureEnabled } from "../../../app/externalApi/VelocityApi";
import { formatString } from "../../../app/utils/stringUtilities";
const TelemetryModule = import(
/* webpackChunkName: "Telemetry-Chunk" */
"../../../telemetry/Telemetry");
async function updateWidgetContent(widgetContent, widgetContentContainerRef) {
    const node = widgetContentContainerRef.current;
    if (!node.hasChildNodes()) {
        node.appendChild(widgetContent);
    }
}
export const FeedContainer = React.forwardRef((props, ref) => {
    const [feedInstance, setFeedInstance] = useState();
    const feedsContentContainerRef = useRef(null);
    const signalApi = window.__com_microsoft_dsh_widgetModelApiFactory.tryGetApi("signals", 1, 1);
    function tryCreateFeedsInstance() {
        getFeedsInstancePromise().then((instance) => {
            if (instance === undefined) {
                lazyLoadModule(TelemetryModule).then((Telemetry) => {
                    Telemetry.logEvent(dashboard, "FailedToRetrieveFeedInstance", 16777216 /* productAndServicePerformance */);
                });
                return;
            }
            lazyLoadModule(TelemetryModule).then((Telemetry) => {
                Telemetry.logEvent(dashboard, "FeedContainerComponentCreated", 16777216 /* productAndServicePerformance */);
            });
            setFeedInstance(instance);
        });
    }
    useEffect(() => {
        tryCreateFeedsInstance();
    }, []);
    useEffect(() => {
        if (feedInstance) {
            getWidgetElementAsync(feedInstance).then((feedContent) => updateWidgetContent(feedContent, feedsContentContainerRef).then(() => {
                lazyLoadModule(TelemetryModule).then((Telemetry) => {
                    Telemetry.logEvent(dashboard, "FeedContainerComponentCreated", 16777216 /* productAndServicePerformance */);
                });
            }));
        }
    }, [feedInstance]);
    const contentReadyContext = useContext(ContentReadyContext);
    useEffect(() => {
        function handleIntersection(entries) {
            if (!feedInstance) {
                return;
            }
            getIsWindowVisible().then((isWindowVisible) => {
                entries.forEach((entry) => {
                    // ignore isWindowVisible for this telemetry, which indicates if a widget is or is not visible even if the window is hidden
                    // Calculate currentVisibility based on intersection ratio > 0 (it's possible for isIntersecting = true && intersectionRatio = 0)
                    let currentVisibility = entry.intersectionRatio > 0 ? 2 /* AboveTheFold */ : 1 /* BelowTheFold */;
                    let currentPercentVisible = entry.intersectionRatio.toString();
                    Measures.logEvent(dashboard, "WidgetVisibility", 16777216 /* productAndServicePerformance */, {
                        appId: feedInstance.widgetRegistration.appId,
                        indexedDBInstanceId: feedInstance.instanceId,
                        newPercentVisible: currentPercentVisible,
                        newVisibility: currentVisibility,
                        oldPercentVisible: feedInstance.telemetry.percentVisible,
                        oldVisibility: feedInstance.telemetry.visibility,
                        providerId: feedInstance.widgetRegistration.providerId,
                        widgetDefinitionId: feedInstance.widgetRegistration.id,
                        widgetInstanceId: feedInstance.widgetId,
                    });
                    feedInstance.telemetry.visibility = currentVisibility;
                    feedInstance.telemetry.percentVisible = currentPercentVisible;
                    if (entry.isIntersecting) {
                        isFeedIntersectedWithVisibleRoot = true;
                        // Don't signal to widgets if the window isn't visible. Once it becomes visible we will signal via the visibilityEventListeners
                        if (isWindowVisible) {
                            onWidgetVisible(feedInstance);
                        }
                    }
                    else if (!entry.isIntersecting) {
                        isFeedIntersectedWithVisibleRoot = false;
                        if (isWindowVisible) {
                            onWidgetHidden(feedInstance);
                        }
                    }
                });
            }, (reason) => {
                // If windowAPI is unavailable, the safest option is to signal to widgets as though the window is visible
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        isFeedIntersectedWithVisibleRoot = true;
                        onWidgetVisible(feedInstance);
                    }
                    else if (!entry.isIntersecting) {
                        isFeedIntersectedWithVisibleRoot = false;
                        onWidgetHidden(feedInstance);
                    }
                });
            });
        }
        // Boolean tracking if the feed is inside the visible frame. It is not component state and must be synchronous, so don't leverage useState
        let isFeedIntersectedWithVisibleRoot = false;
        if (feedInstance) {
            addVisibilityEventListeners(() => {
                if (isFeedIntersectedWithVisibleRoot) {
                    onWidgetVisible(feedInstance);
                }
            }, () => {
                // Only signal to intersected feed because if the feed is not currently intersected then it should have already been signaled to.
                if (isFeedIntersectedWithVisibleRoot) {
                    onWidgetHidden(feedInstance);
                }
            });
            feedInstance.addStateChangeListener((widgetInstance, previousState, currentState) => {
                Measures.logEvent(dashboard, "FeedStateChanged", 16777216 /* productAndServicePerformance */, {
                    currentState: currentState,
                    errorStateCode: widgetInstance.widgetErrorCode,
                    errorStateReason: widgetInstance.widgetErrorStateReason,
                    previousState: previousState,
                });
                updateFromWidgetState();
            });
            updateFromWidgetState();
        }
        // We cannot use getWidgetElementAsync because it doesn't return visible HTML
        const feedHTML = document.getElementById("feed-container");
        const observer = new IntersectionObserver(handleIntersection);
        if (feedHTML) {
            observer.observe(feedHTML);
        }
        return function unsubscribeEventListeners() {
            if (feedHTML) {
                observer.unobserve(feedHTML);
            }
        };
    }, [feedInstance]);
    // Ensure that clicks/taps outside the FocusTrapZone disable the FocusTrapZone.
    useEffect(() => {
        function handlePointerOutside(event) {
            if (feedsContentContainerRef.current && !feedsContentContainerRef.current.contains(event.target)) {
                setFocusTrapDisabled(true);
            }
        }
        document.addEventListener("pointerdown", handlePointerOutside);
        return function cleanup() {
            document.removeEventListener("pointerdown", handlePointerOutside);
        };
    }, [feedsContentContainerRef]);
    const [showErrorPage, setShowErrorPage] = useState(feedInstance ? feedInstance.widgetState === "error" : false);
    // Update the state of the container based on the current state of the associated widget.
    function updateFromWidgetState() {
        if (feedInstance) {
            const widgetState = feedInstance.widgetState;
            Trace.logEvent(dashboard, "FeedContainerUpdateFromWidgetState", 0 /* verbose */, {
                widgetState: widgetState,
            });
            let stateSignal = "";
            switch (widgetState) {
                case "loading":
                    stateSignal = "Loading";
                    setShowErrorPage(false);
                    break;
                case "error":
                    stateSignal = "Error";
                    contentReadyContext.setIsFeedReady(true);
                    setShowErrorPage(true);
                    break;
                case "normal":
                    stateSignal = "Normal";
                    contentReadyContext.setIsFeedReady(true);
                    setShowErrorPage(false);
                    break;
            }
            if (signalApi && stateSignal !== "") {
                let payload = {
                    widgetItem: {
                        appId: feedInstance.widgetRegistration.appId,
                        indexedDBInstanceId: feedInstance.instanceId.toString(),
                        providerId: feedInstance.widgetRegistration.providerId,
                        widgetDefinitionId: feedInstance.widgetDefinitionId,
                        widgetInstanceId: feedInstance.widgetId,
                        widgetState: stateSignal,
                    },
                };
                let signal = { name: "WidgetStateUpdated", payload: JSON.stringify(payload), signalType: "WDX" };
                signalApi.sendSignal(signal);
            }
        }
    }
    const feedFocusTrapZoneRef = useRef(null);
    const feedFocusTrapZoneElementRef = useRef(null);
    // state used to manage whether the focusTrap for tabbing within the WidgetFrame is enabled.
    const [focusTrapDisabled, setFocusTrapDisabled] = useState(true);
    function onFocusTrapZoneKeyDown(event) {
        var _a, _b;
        // Don't allow key-down events to bubble up from the FocusTrapZone when enabled.
        // Keys should be reserved only for interacting with the content within the FocusTrapZone
        // when enabled.
        if (!focusTrapDisabled) {
            event.stopPropagation();
        }
        if (event.key === "Enter" || event.key === " ") {
            if (focusTrapDisabled && document.activeElement === feedFocusTrapZoneElementRef.current) {
                setFocusTrapDisabled(false);
                (_a = feedFocusTrapZoneRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                event.preventDefault(); // Prevent default Space behavior of scrolling down.
                event.stopPropagation();
            }
        }
        else if (event.key === "Escape") {
            if (!focusTrapDisabled) {
                setFocusTrapDisabled(true);
                (_b = feedFocusTrapZoneElementRef.current) === null || _b === void 0 ? void 0 : _b.focus();
            }
        }
    }
    const focusZoneRef = useRef(null);
    useImperativeHandle(ref, () => {
        var _a, _b;
        return ({
            focusZone: (_b = (_a = focusZoneRef.current) === null || _a === void 0 ? void 0 : _a.focusZone) !== null && _b !== void 0 ? _b : null,
        });
    });
    async function retry() {
        if (feedInstance) {
            Measures.logEvent(dashboard, "FeedReloadClicked", 33554432 /* productAndServiceUsage */, {
                appId: feedInstance.widgetRegistration.appId,
                indexedDBInstanceId: feedInstance.instanceId,
                providerId: feedInstance.widgetRegistration.providerId,
                widgetDefinitionId: feedInstance.widgetRegistration.id,
                widgetInstanceId: feedInstance.widgetId,
            });
            let orchestrator = await getWidgetOrchestrator();
            orchestrator.reloadFailedWidgets(null, true);
        }
    }
    const theme = useTheme();
    const isBoardsLiteEnabled = isFeatureEnabled("Feature_BoardsLite");
    return (React.createElement(FocusZone, { previousFocusableElement: null, nextFocusableElement: null, ref: focusZoneRef, firstZoneFocusableElement: feedFocusTrapZoneElementRef.current },
        React.createElement(Stack, { styles: getContainerStyle() },
            React.createElement(FocusTrapZone, { componentRef: feedFocusTrapZoneRef, ref: feedFocusTrapZoneElementRef, className: "widgetFocusTrapZone", disabled: focusTrapDisabled, onKeyDown: onFocusTrapZoneKeyDown, style: { borderRadius: "8px" }, tabIndex: -1, ignoreExternalFocusing: true, role: "region", "aria-label": isBoardsLiteEnabled
                    ? formatString(getResources().WidgetFrame_BoardFrameAriaLabel, { name: feedInstance === null || feedInstance === void 0 ? void 0 : feedInstance.widgetRegistration.displayName }).toString()
                    : getResources().WidgetFrame_FeedFrameAriaLabel },
                React.createElement(Stack, { role: "document" },
                    showErrorPage && (React.createElement(ErrorComponent, { reloadClick: retry, styles: getFeedErrorComponentStyle(theme), mainText: getResources().WidgetOfflinePage_OfflineErrorMessage, subtext: getResources().FeedOfflinePage_OfflineErrorSubtext, buttonText: getResources().WidgetOfflinePage_ReloadButtonText })),
                    React.createElement(Stack.Item, { verticalFill: !showErrorPage, styles: showErrorPage ? getFeedErrorStateStyle() : undefined, role: "region", "aria-label": isBoardsLiteEnabled
                            ? getResources().WidgetFrame_BoardFrameBumper_AriaLabel
                            : getResources().WidgetFrame_FeedFrameBumper_AriaLabel },
                        React.createElement("div", { ref: feedsContentContainerRef, style: getFeedContainerAndWidgetFrameBumperStyle(), tabIndex: -1 })))))));
});
